import { useEffect, useState } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle, X, Clock, ArrowUp, Trash, FileText, Sparkle } from "@phosphor-icons/react";

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function statusTone(s) {
  if (s === "verified") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "rejected") return "bg-red-50 text-red-700 border-red-200";
  if (s === "pending_verification") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-zinc-50 text-zinc-600 border-zinc-200";
}

export default function JoinerPortal() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/onboarding/me/current");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const onPick = async (doc_type, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5MB)");
    setUploading((u) => ({ ...u, [doc_type]: true }));
    try {
      const file_data_b64 = await readFileAsBase64(file);
      await api.post("/onboarding/me/documents", {
        doc_type, file_name: file.name, file_data_b64,
        mime_type: file.type || "application/pdf",
      });
      toast.success(`${file.name} uploaded`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [doc_type]: false }));
    }
  };

  const deleteDoc = async (doc_id) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/onboarding/me/documents/${doc_id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  if (!data) {
    return <AppShell title="Welcome"><div className="py-12 text-center text-zinc-500">Loading…</div></AppShell>;
  }

  const catalog = data.doc_catalog || [];
  const docsByType = Object.fromEntries((data.documents || []).map((d) => [d.doc_type, d]));
  const requiredCount = catalog.filter((c) => c.required).length;
  const requiredUploaded = catalog.filter((c) => c.required && docsByType[c.key]).length;
  const allVerified = catalog.filter((c) => c.required).every((c) => docsByType[c.key]?.status === "verified");

  const ob = data.onboarding;
  const kit = data.kit_issuance;

  return (
    <AppShell title="Welcome aboard">

      {/* Welcome hero */}
      <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-xl p-6 mb-6" data-testid="joiner-hero">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-700 font-semibold">
          <Sparkle size={14} weight="duotone" /> Welcome to the team
        </div>
        <h1 className="font-display text-2xl mt-2">A few things to wrap up before day one.</h1>
        <p className="text-sm text-zinc-700 mt-2 max-w-2xl">
          Upload your documents, see your onboarding checklist, and check what's in your joining kit. Your HR team
          will verify everything and ping you if anything needs attention.
        </p>
        <div className="mt-4 flex items-center gap-4 text-sm">
          <Badge variant="outline" className="bg-white" data-testid="docs-progress">
            {requiredUploaded} / {requiredCount} required docs uploaded
          </Badge>
          {allVerified && requiredCount > 0 && (
            <Badge className="bg-emerald-600 text-white" data-testid="all-verified-badge">
              <CheckCircle size={12} weight="bold" className="mr-1" /> All required verified
            </Badge>
          )}
        </div>
      </div>

      {/* Onboarding checklist */}
      {ob && (
        <SectionCard
          title="Your onboarding checklist"
          subtitle={`Joining ${ob.date_of_joining} · ${ob.tasks?.length || 0} tasks · ${ob.tasks?.filter((t) => t.status === "done").length || 0} done`}
          testid="joiner-checklist"
        >
          <div className="space-y-2">
            {(ob.tasks || []).slice(0, 8).map((t) => (
              <div key={t.task_id} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0" data-testid={`joiner-task-${t.task_id}`}>
                {t.status === "done" ? (
                  <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0" />
                ) : (
                  <Clock size={18} className="text-zinc-300 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${t.status === "done" ? "line-through text-zinc-400" : ""}`}>{t.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Owner: <span className="capitalize">{t.assignee}</span> · Due {t.due_date}</div>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase">{t.status.replace("_", " ")}</Badge>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Document upload */}
      <SectionCard
        title="Documents"
        subtitle="Drag-drop or pick a file. PDF or image, max 5 MB each. Replace any time before HR verifies."
        testid="joiner-docs"
      >
        <div className="grid md:grid-cols-2 gap-3">
          {catalog.map((c) => {
            const uploaded = docsByType[c.key];
            const isUploading = uploading[c.key];
            return (
              <div key={c.key} className={`border rounded-lg p-4 ${uploaded ? "bg-white border-zinc-200" : "bg-zinc-50 border-zinc-200 border-dashed"}`} data-testid={`doc-row-${c.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      {c.label}
                      {c.required && <span className="text-red-500 text-xs">*</span>}
                    </div>
                    {uploaded && (
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
                          <FileText size={11} /> {uploaded.file_name}
                        </span>
                        <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 rounded ${statusTone(uploaded.status)}`}>
                          {uploaded.status.replace("_", " ")}
                        </span>
                      </div>
                    )}
                    {uploaded?.status === "rejected" && uploaded?.rejection_reason && (
                      <div className="mt-1 text-xs text-red-600 italic">"{uploaded.rejection_reason}"</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {uploaded && uploaded.status !== "verified" && (
                      <Button variant="ghost" size="icon" onClick={() => deleteDoc(uploaded.id)} data-testid={`delete-${c.key}`}>
                        <Trash size={14} className="text-zinc-500" />
                      </Button>
                    )}
                    <label className={`inline-flex items-center gap-1.5 text-xs font-medium border border-zinc-300 px-3 py-1.5 rounded-md cursor-pointer hover:bg-zinc-50 ${isUploading ? "opacity-50 pointer-events-none" : ""}`} data-testid={`upload-${c.key}`}>
                      <ArrowUp size={12} weight="bold" />
                      {isUploading ? "Uploading…" : uploaded ? "Replace" : "Upload"}
                      <input type="file" className="hidden" onChange={(e) => onPick(c.key, e)} accept=".pdf,.jpg,.jpeg,.png" disabled={isUploading} />
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Joining kit */}
      {kit && (
        <SectionCard
          title="Your joining kit"
          subtitle={kit.template_name + " · Status: " + kit.status}
          testid="joiner-kit"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(kit.items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-md px-3 py-2" data-testid={`kit-item-${i}`}>
                <div className="w-8 h-8 bg-amber-50 rounded-md flex items-center justify-center shrink-0">
                  <FileText size={16} className="text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="text-xs text-zinc-500">Qty {it.qty}{it.is_returnable ? " · Returnable" : ""}</div>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase">{it.status}</Badge>
              </div>
            ))}
            {(!kit.items || kit.items.length === 0) && (
              <div className="text-sm text-zinc-500 py-4 text-center col-span-2">No kit items yet — HR will assign soon.</div>
            )}
          </div>
        </SectionCard>
      )}

      {!ob && (
        <SectionCard title="Onboarding not started yet" testid="no-onboarding">
          <p className="text-sm text-zinc-600">
            Your HR team hasn't kicked off the onboarding workflow yet. You can still upload your documents
            now — they'll be ready when your onboarding begins.
          </p>
        </SectionCard>
      )}
    </AppShell>
  );
}
